import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Translate as TranslateIcon,
} from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { fetchLanguageTranslationsByCode } from '../../api/languageTranslations';
import { AppLanguage } from '../../types/Language';
import { useLanguages } from '../../hooks/useLanguages';

interface LanguageFormData {
  code: string;
  label: string;
  sortOrder: number;
  isActive: boolean;
  isRequired: boolean;
}

const defaultFormData: LanguageFormData = {
  code: '',
  label: '',
  sortOrder: 0,
  isActive: true,
  isRequired: false,
};

const LanguagesPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { languages, isLoading, error } = useLanguages({ includeInactive: true });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingLanguage, setEditingLanguage] = useState<AppLanguage | null>(null);
  const [languageToDelete, setLanguageToDelete] = useState<AppLanguage | null>(null);
  const [formData, setFormData] = useState<LanguageFormData>(defaultFormData);
  const [formError, setFormError] = useState('');
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const saveMutation = useMutation({
    mutationFn: async (data: LanguageFormData) => {
      const payload = {
        code: data.code.trim(),
        label: data.label.trim(),
        sortOrder: data.sortOrder,
        isActive: data.isActive,
        isRequired: data.isRequired,
      };

      if (editingLanguage) {
        return axios.put(
          `${process.env.REACT_APP_API_URL}/languages/${editingLanguage.id}`,
          payload
        );
      }

      let translations = {};
      try {
        translations = await fetchLanguageTranslationsByCode('de');
      } catch {
        translations = {};
      }

      return axios.post(`${process.env.REACT_APP_API_URL}/languages`, {
        ...payload,
        translations,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['languages'] });
      setDialogOpen(false);
      setEditingLanguage(null);
      setFormData(defaultFormData);
      setFormError('');
      setNotification({
        open: true,
        message: editingLanguage
          ? t('languages.languageUpdated')
          : t('languages.languageCreated'),
        severity: 'success',
      });
    },
    onError: (err: unknown) => {
      const axiosError = err as { response?: { data?: { error?: string }; status?: number } };
      if (axiosError.response?.status === 409) {
        setFormError(t('languages.codeConflict'));
      } else {
        setFormError(axiosError.response?.data?.error || t('languages.saveFailed'));
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return axios.delete(`${process.env.REACT_APP_API_URL}/languages/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['languages'] });
      setDeleteDialogOpen(false);
      setLanguageToDelete(null);
      setNotification({
        open: true,
        message: t('languages.languageDeleted'),
        severity: 'success',
      });
    },
    onError: (err: unknown) => {
      const axiosError = err as { response?: { data?: { error?: string }; status?: number } };
      setDeleteDialogOpen(false);
      setLanguageToDelete(null);
      setNotification({
        open: true,
        message:
          axiosError.response?.status === 409
            ? t('languages.deleteRequired')
            : axiosError.response?.data?.error || t('languages.deleteFailed'),
        severity: 'error',
      });
    },
  });

  const openCreateDialog = () => {
    setEditingLanguage(null);
    setFormData(defaultFormData);
    setFormError('');
    setDialogOpen(true);
  };

  const openEditDialog = (language: AppLanguage) => {
    setEditingLanguage(language);
    setFormData({
      code: language.code,
      label: language.label,
      sortOrder: language.sortOrder ?? 0,
      isActive: language.isActive !== false,
      isRequired: language.isRequired !== false,
    });
    setFormError('');
    setDialogOpen(true);
  };

  const handleSave = () => {
    if (!formData.code.trim() || !formData.label.trim()) {
      setFormError(t('languages.requiredFields'));
      return;
    }

    if (!/^[a-z]{2}(-[a-z]{2})?$/.test(formData.code.trim())) {
      setFormError(t('languages.codeFormat'));
      return;
    }

    saveMutation.mutate(formData);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{t('languages.errorLoading')}</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">{t('languages.title')}</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
          {t('languages.addLanguage')}
        </Button>
      </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('languages.code')}</TableCell>
                <TableCell>{t('languages.label')}</TableCell>
                <TableCell align="center">{t('languages.sortOrder')}</TableCell>
                <TableCell align="center">{t('languages.status')}</TableCell>
                <TableCell align="center">{t('languages.required')}</TableCell>
                <TableCell align="right">{t('locations.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {languages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    {t('languages.noLanguages')}
                  </TableCell>
                </TableRow>
              ) : (
                languages.map((language) => (
                  <TableRow key={language.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {language.code}
                      </Typography>
                    </TableCell>
                    <TableCell>{language.label}</TableCell>
                    <TableCell align="center">{language.sortOrder ?? 0}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={
                          language.isActive !== false
                            ? t('languages.active')
                            : t('languages.inactive')
                        }
                        size="small"
                        color={language.isActive !== false ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={
                          language.isRequired !== false
                            ? t('common.yes')
                            : t('common.no')
                        }
                        size="small"
                        color={language.isRequired !== false ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/languages/${language.id}/translations`)}
                        aria-label={t('languages.editTranslations')}
                      >
                        <TranslateIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => openEditDialog(language)}
                        aria-label={t('common.edit')}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setLanguageToDelete(language);
                          setDeleteDialogOpen(true);
                        }}
                        aria-label={t('common.delete')}
                        disabled={language.isRequired !== false}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingLanguage ? t('languages.editLanguage') : t('languages.addLanguage')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {formError && <Alert severity="error">{formError}</Alert>}
            <TextField
              label={t('languages.code')}
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              fullWidth
              required
              disabled={!!editingLanguage}
              helperText={t('languages.codeHelp')}
            />
            <TextField
              label={t('languages.label')}
              value={formData.label}
              onChange={(e) => setFormData({ ...formData, label: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label={t('languages.sortOrder')}
              type="number"
              value={formData.sortOrder}
              onChange={(e) =>
                setFormData({ ...formData, sortOrder: parseInt(e.target.value, 10) || 0 })
              }
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label={t('languages.active')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isRequired}
                  onChange={(e) => setFormData({ ...formData, isRequired: e.target.checked })}
                  disabled={editingLanguage?.isRequired !== false}
                />
              }
              label={t('languages.required')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleSave} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? t('common.loading') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('languages.confirmDelete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('languages.deleteConfirmation', { label: languageToDelete?.label })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => languageToDelete && deleteMutation.mutate(languageToDelete.id)}
            disabled={deleteMutation.isPending}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert
          severity={notification.severity}
          onClose={() => setNotification({ ...notification, open: false })}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LanguagesPage;
